import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import WorkEntryForm from '../components/WorkEntryForm';
import WorkEntryTable from '../components/WorkEntryTable';
import ReportsPanel from '../components/ReportsPanel';
import FilterPanel from '../components/FilterPanel';
import AllJobsView from '../components/AllJobsView';
import TodaysJobsView from '../components/TodaysJobsView';
import ColorSchemeSelector from '../components/ColorSchemeSelector';
import ExportModal from '../components/ExportModal';
import MyAvailability from '../components/MyAvailability';
import { useBusinessName } from '../contexts/BusinessContext';
import { AdminJobForm } from '../components/AdminJobForm';

export default function Dashboard() {
  const [entries, setEntries] = useState([]);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [filters, setFilters] = useState({});
  const [view, setView] = useState('jobs'); // 'today', 'jobs', 'entries', 'reports', 'settings', or 'availability'
  const [showExportModal, setShowExportModal] = useState(false);
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobsRevision, setJobsRevision] = useState(0);
  const navigate = useNavigate();
  const { businessName } = useBusinessName();

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchEntries();
    fetchReports();
  }, [filters]);

  const fetchEntries = async () => {
    try {
      const params = new URLSearchParams(filters);
      const response = await axios.get(`${API}/entries?${params}`);
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to fetch entries');
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports`);
      setReports(response.data);
    } catch (error) {
      console.error('Failed to fetch reports');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('DELETE THIS ENTRY? [Y/N]')) return;

    try {
      await axios.delete(`${API}/entries/${entryId}`);
      toast.success('Entry deleted');
      fetchEntries();
      fetchReports();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingEntry(null);
    fetchEntries();
    fetchReports();
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/export/excel`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'work_entries.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel file downloaded!');
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };

  const handleExportEmail = () => {
    setShowExportModal(true);
  };

  const openJobForm = (job = null) => {
    setEditingJob(job);
    setShowJobForm(true);
  };

  const jobSaved = () => {
    if (!editingJob) setView('jobs');
    setShowJobForm(false);
    setEditingJob(null);
    setJobsRevision(value => value + 1);
  };

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ background: '#000000' }}>
      {/* Header */}
      <div className="retro-panel mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold glow-text" style={{ color: '#FF6B00', fontFamily: 'Orbitron, sans-serif' }}>
              {businessName}
            </h1>
            <div className="text-lg mt-1" style={{ color: '#ffffff' }}>
              USER: {user.username} | STATUS: <span style={{ color: '#FF6B00' }}>ONLINE</span>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="retro-btn retro-btn-magenta"
          >
            [LOGOUT]
          </button>
        </div>
      </div>

      {/* View Toggle */}
      <div className="retro-panel mb-6">
        <div className="flex flex-wrap gap-4">
          {user.is_admin && <button type="button" onClick={() => openJobForm()} data-testid="dashboard-create-job" className="retro-btn retro-btn-green">[+ CREATE JOB]</button>}
          {user.is_admin && (
            <button
              onClick={() => navigate('/admin')}
              data-testid="go-to-admin-button"
              className="retro-btn retro-btn-yellow"
            >
              [ADMIN PANEL]
            </button>
          )}
          <button
            onClick={() => setView('today')}
            data-testid="view-today-button"
            className={`retro-btn ${view === 'today' ? 'retro-btn-green' : ''}`}
            style={{ backgroundColor: view === 'today' ? '#00FF00' : undefined }}
          >
            [TODAY'S JOBS]
          </button>
          <button
            onClick={() => setView('jobs')}
            data-testid="view-jobs-button"
            className={`retro-btn ${view === 'jobs' ? 'retro-btn-green' : ''}`}
          >
            [JOBS]
          </button>
          <button
            onClick={() => setView('entries')}
            data-testid="view-entries-button"
            className={`retro-btn ${view === 'entries' ? 'retro-btn-green' : ''}`}
          >
            [WORK ENTRIES]
          </button>
          <button onClick={() => navigate('/warranties')} data-testid="view-warranties-button" className="retro-btn">[WARRANTIES]</button>
          <button
            onClick={() => setView('reports')}
            data-testid="view-reports-button"
            className={`retro-btn ${view === 'reports' ? 'retro-btn-green' : ''}`}
          >
            [REPORTS]
          </button>
          <button
            onClick={() => setView('availability')}
            data-testid="my-availability-button"
            className={`retro-btn ${view === 'availability' ? 'retro-btn-green' : ''}`}
          >
            [MY AVAILABILITY]
          </button>
          <button
            onClick={() => setView('settings')}
            data-testid="view-settings-button"
            className={`retro-btn ${view === 'settings' ? 'retro-btn-green' : ''}`}
          >
            [SETTINGS]
          </button>
          <button
            onClick={() => {
              setEditingEntry(null);
              setShowForm(!showForm);
            }}
            data-testid="add-entry-button"
            className="retro-btn"
          >
            {showForm ? '[CANCEL]' : '[+ NEW ENTRY]'}
          </button>
          <button
            onClick={handleExportExcel}
            data-testid="export-excel-button"
            className="retro-btn retro-btn-yellow"
          >
            [EXPORT EXCEL]
          </button>
          <button
            onClick={handleExportEmail}
            data-testid="export-email-button"
            className="retro-btn retro-btn-yellow"
          >
            [EMAIL EXPORT]
          </button>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="mb-6">
          <WorkEntryForm
            entry={editingEntry}
            onSuccess={handleFormSuccess}
            onCancel={() => {
              setShowForm(false);
              setEditingEntry(null);
            }}
          />
        </div>
      )}

      {/* Content */}
      {view === 'today' && <TodaysJobsView onEditJob={openJobForm} refreshKey={jobsRevision} />}
      {view === 'jobs' && <AllJobsView onEditJob={openJobForm} refreshKey={jobsRevision} />}
      {view === 'entries' && (
        <>
          <FilterPanel filters={filters} setFilters={setFilters} />
          <WorkEntryTable
            entries={entries}
            loading={loading}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </>
      )}
      {view === 'reports' && <ReportsPanel reports={reports} />}
      {view === 'availability' && <MyAvailability />}
      {view === 'settings' && <ColorSchemeSelector />}

      {/* Export Modal */}
      {user.is_admin && showJobForm && <AdminJobForm appointment={editingJob} onSuccess={jobSaved} onCancel={() => { setShowJobForm(false); setEditingJob(null); }} />}
      {showExportModal && (
        <ExportModal
          onClose={() => setShowExportModal(false)}
          userEmail={user.email}
        />
      )}
    </div>
  );
}